import type { MatchCalendarFormat, MatchWording } from '@uefadigital/panenka';
import { MatchUnitSize } from '@uefadigital/panenka';

import EventBusInstance from '@core/eventbus';
import type { MatchUnitServiceOptions } from '@services/MatchUnitService';
import matchUnitServiceInstance from '@services/MatchUnitService';
import TranslatorServiceInstance, { hydrateWithTranslations } from '@services/TranslatorService';
import type { Match } from '@shared-types/match.types';
import { MatchStatus } from '@shared-types/match.types';
import GoogleTagManagerHelper from '@tracking/gtm-helper';
import { getMatchTracking } from '@utilities/match-tracking';
import { parseToObject } from '@utilities/parsing';
import D3Throttle from '@utilities/throttle';

const labelMap = {
  [MatchStatus.UPCOMING]: 'upcoming_match',
  [MatchStatus.LIVE]: 'livescores',
  [MatchStatus.FINISHED]: 'highlights',
} as const;

export type PkMatchUnitPluginOptions = MatchUnitServiceOptions;

export default class PkMatchUnitPlugin {
  options: PkMatchUnitPluginOptions;

  dom: HTMLElement;
  matchUnit: HTMLPkMatchUnitElement;
  initialSize?: MatchUnitSize;

  throttle: D3Throttle;

  constructor(dom: HTMLElement, o: PkMatchUnitPluginOptions) {
    const defaults: PkMatchUnitPluginOptions = {};

    this.options = {
      ...defaults,
      ...o,
    };

    this.dom = dom;
    this.matchUnit = this.dom.matches('pk-match-unit')
      ? (this.dom as HTMLPkMatchUnitElement)
      : this.dom.querySelector('pk-match-unit');

    this.matchUnit.translations = parseToObject<MatchWording>(this.matchUnit.getAttribute('translations'));
    this.matchUnit.dateCalendarFormat = parseToObject<MatchCalendarFormat>(
      this.matchUnit.getAttribute('date-calendar-format'),
    );

    this.matchUnit.addEventListener('pkCtaClick', this.trackCtaClick);
  }

  init() {
    this.initialSize = this.matchUnit.size;
    if (this.initialSize === MatchUnitSize.XL) {
      const events = ['orientationchange.matchunit', 'resize.matchunit'];
      this.throttle = new D3Throttle(window, this.checkWidth, undefined, events, this);
      this.checkWidth();
    }

    matchUnitServiceInstance.on(this.options, this.handler, this);
  }

  detach() {
    this.throttle && this.throttle.off();
    matchUnitServiceInstance.off(this.options, this.handler, this);

    this.matchUnit.removeEventListener('pkCtaClick', this.trackCtaClick);
  }

  handler(match: Match) {
    this.matchUnit.matchData = match;
    [this.matchUnit, this.dom].forEach((e) => (e.dataset.status = match.status));
    this.setTracking(match);

    EventBusInstance.dispatch('matchunit.updated');
    this.setAriaLabel(match);
  }

  checkWidth() {
    if (this.initialSize && this.initialSize !== MatchUnitSize.XL) return;

    const isMobile = screen.availWidth < 768;
    this.matchUnit.size = isMobile ? MatchUnitSize.M : MatchUnitSize.XL;
    [this.matchUnit, this.dom].forEach((e) => e.classList.toggle('mu-horizontal--xl', !isMobile));
    [this.matchUnit, this.dom].forEach((e) => e.classList.toggle('mu-horizontal--m', isMobile));
  }

  setTracking = (match: Match) => {
    const tracking = getMatchTracking(match, this.matchUnit.dataset.hasHighlights === 'true');
    this.dom.dataset.tracking = JSON.stringify(tracking);
  };

  trackCtaClick = (e: CustomEvent) => {
    e.stopPropagation();

    const match = this.matchUnit.matchData as Match;
    const tracking = getMatchTracking(match, null, 'see more');
    if (!tracking) return;
    GoogleTagManagerHelper.gtmCardElementClick(e.target as HTMLElement, tracking);
  };

  setAriaLabel = async (match: Match) => {
    const matchStatus = labelMap[match.status] && (await TranslatorServiceInstance.getText(labelMap[match.status]));
    const homeTeam = hydrateWithTranslations(match.homeTeam)?.displayName;
    const awayTeam = hydrateWithTranslations(match.awayTeam)?.displayName;
    const ctaAriaLabel = [matchStatus, homeTeam, awayTeam].filter(Boolean).join(' - ');
    this.dom.title = ctaAriaLabel;
    this.matchUnit.ctaAriaLabel = ctaAriaLabel;
  };
}
